import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { 
  Syringe, FileText, TestTube, ChevronRight, 
  AlertCircle, CheckCircle, Clock, Calendar
} from "lucide-react";

// Semantic colors from Apple Health
const STATUS_COLORS = {
  valid: {
    bg: "bg-emerald-50 dark:bg-emerald-500/10",
    text: "text-emerald-600 dark:text-emerald-400",
    icon: "text-emerald-500",
    badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
  },
  expiring: {
    bg: "bg-amber-50 dark:bg-amber-500/10",
    text: "text-amber-600 dark:text-amber-400",
    icon: "text-amber-500",
    badge: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
  },
  expired: {
    bg: "bg-red-50 dark:bg-red-500/10",
    text: "text-red-600 dark:text-red-400",
    icon: "text-red-500",
    badge: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"
  },
  neutral: {
    bg: "bg-slate-50 dark:bg-slate-500/10",
    text: "text-slate-600 dark:text-slate-400",
    icon: "text-slate-500",
    badge: "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300"
  }
};

export function HealthStatusCard({ 
  icon: Icon, 
  title, 
  status, 
  statusText, 
  action, 
  actionText,
  counts,
  onClick,
  testId
}) {
  const colorScheme = STATUS_COLORS[status] || STATUS_COLORS.neutral;
  
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`
        w-full text-left p-5 rounded-3xl 
        bg-white dark:bg-[#1C1C1E] 
        border border-black/5 dark:border-white/10
        shadow-sm hover:shadow-md
        transition-all duration-200 ease-out
        active:scale-[0.98]
        group
      `}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`
          w-12 h-12 rounded-2xl flex items-center justify-center
          ${colorScheme.bg}
        `}>
          <Icon className={colorScheme.icon} size={24} />
        </div>
        <ChevronRight 
          className="text-slate-300 dark:text-slate-600 group-hover:translate-x-1 transition-transform" 
          size={20} 
        />
      </div>
      
      <h3 className="text-lg font-semibold text-[#1D1D1F] dark:text-white mb-1">
        {title}
      </h3>
      
      {counts && (
        <div className="flex gap-2 mb-2 flex-wrap">
          {counts.valid > 0 && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS.valid.badge}`}>
              {counts.valid} validi
            </span>
          )}
          {counts.expiring > 0 && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS.expiring.badge}`}>
              {counts.expiring} in scadenza
            </span>
          )}
          {counts.expired > 0 && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS.expired.badge}`}>
              {counts.expired} scaduti
            </span>
          )}
        </div>
      )}
      
      <p className={`text-sm ${colorScheme.text} mb-2`}>
        {statusText}
      </p>
      
      {action && (
        <div className="flex items-center gap-1 text-[#007AFF] text-sm font-medium">
          {actionText}
          <ChevronRight size={14} />
        </div>
      )}
    </button>
  );
}

export function VaccineStatusCard({ petId, vaccines = [], onClick }) {
  // Calculate vaccine statistics
  const now = new Date();
  let valid = 0;
  let expiring = 0;
  let expired = 0;
  let nextAction = null;
  
  // Group by category to check only latest per type
  const byCategory = {};
  vaccines.forEach(v => {
    const cat = v.vaccine_category || v.vaccine_type || 'other';
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(v);
  });
  
  Object.values(byCategory).forEach(catVaccines => {
    // Sort by date, get latest
    catVaccines.sort((a, b) => {
      const dateA = new Date(a.date_administered || 0);
      const dateB = new Date(b.date_administered || 0);
      return dateB - dateA;
    });
    
    const latest = catVaccines[0];
    if (!latest.expiry_date) {
      valid++;
      return;
    }
    
    const expiry = new Date(latest.expiry_date);
    const daysUntil = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) {
      expired++;
      if (!nextAction || daysUntil > nextAction.days) {
        nextAction = {
          name: latest.name || latest.vaccine_name || "Vaccino",
          days: daysUntil,
          type: 'expired'
        };
      }
    } else if (daysUntil <= 30) {
      expiring++;
      if (!nextAction || nextAction.type !== 'expired') {
        nextAction = {
          name: latest.name || latest.vaccine_name || "Vaccino",
          days: daysUntil,
          type: 'expiring'
        };
      }
    } else {
      valid++;
    }
  });
  
  // Determine overall status
  let status = 'valid';
  let statusText = `${vaccines.length} vaccini registrati`;
  
  if (expired > 0) {
    status = 'expired';
    statusText = nextAction 
      ? `${nextAction.name} scaduto da ${Math.abs(nextAction.days)} giorni`
      : `${expired} vaccin${expired > 1 ? 'i' : 'o'} scadut${expired > 1 ? 'i' : 'o'}`;
  } else if (expiring > 0) {
    status = 'expiring';
    statusText = nextAction
      ? `${nextAction.name} scade tra ${nextAction.days} giorni`
      : `${expiring} vaccin${expiring > 1 ? 'i' : 'o'} in scadenza`;
  } else if (vaccines.length === 0) {
    status = 'neutral';
    statusText = 'Nessun vaccino registrato';
  }
  
  return (
    <HealthStatusCard
      icon={Syringe}
      title="Vaccini"
      status={status}
      statusText={statusText}
      counts={vaccines.length > 0 ? { valid, expiring, expired } : null}
      action={true}
      actionText="Vedi dettagli"
      onClick={onClick}
      testId="health-card-vaccines"
    />
  );
}

export function ExamsStatusCard({ petId, onClick }) {
  const [lastExam, setLastExam] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchExams = async () => {
      try {
        const res = await authFetch(`${API}/exams?pet_id=${petId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.length > 0) {
            // Get most recent exam
            const sorted = data.sort((a, b) => 
              new Date(b.exam_date || b.created_at) - new Date(a.exam_date || a.created_at)
            );
            setLastExam(sorted[0]);
          }
        }
      } catch (e) {
        console.error('Error fetching exams:', e);
      } finally {
        setLoading(false);
      }
    };
    
    if (petId) fetchExams();
  }, [petId]);
  
  let status = 'neutral';
  let statusText = 'Nessun esame registrato';
  
  if (lastExam) {
    const examDate = new Date(lastExam.exam_date || lastExam.created_at);
    const daysAgo = Math.ceil((new Date() - examDate) / (1000 * 60 * 60 * 24));
    
    if (daysAgo <= 90) {
      status = 'valid';
      statusText = `Ultimo controllo ${daysAgo} giorni fa`;
    } else if (daysAgo <= 180) {
      status = 'expiring';
      statusText = `Ultimo controllo ${daysAgo} giorni fa`;
    } else {
      status = 'expired';
      statusText = `Ultimo controllo ${daysAgo} giorni fa`;
    }
  }
  
  return (
    <HealthStatusCard
      icon={TestTube}
      title="Esami"
      status={status}
      statusText={loading ? "Caricamento..." : statusText}
      action={true}
      actionText={lastExam ? "Vedi storico" : "Aggiungi esame"}
      onClick={onClick}
      testId="health-card-exams"
    />
  );
}

export function DocumentsStatusCard({ documents = [], onClick }) {
  // Find passport document
  const passport = documents.find(d => 
    d.document_type === 'passaporto' || 
    d.document_type === 'passport' ||
    (d.filename || '').toLowerCase().includes('passport')
  );
  
  let status = 'neutral';
  let statusText = `${documents.length} documenti caricati`;
  
  if (passport) {
    const uploadDate = new Date(passport.created_at || passport.upload_date);
    const daysAgo = Math.ceil((new Date() - uploadDate) / (1000 * 60 * 60 * 24));
    
    status = 'valid';
    statusText = `Passaporto aggiornato ${daysAgo} giorni fa`;
  } else if (documents.length === 0) {
    status = 'neutral';
    statusText = 'Nessun documento caricato';
  }
  
  return (
    <HealthStatusCard
      icon={FileText}
      title="Documenti"
      status={status}
      statusText={statusText}
      action={true}
      actionText={documents.length > 0 ? "Gestisci" : "Carica documento"}
      onClick={onClick}
      testId="health-card-documents"
    />
  );
}

// Quick Summary Bar (alternative compact view)
export function HealthSummaryBar({ vaccines, documents, onVaccinesClick, onDocsClick }) {
  const now = new Date();
  let vaccineStatus = 'valid';
  
  vaccines.forEach(v => {
    if (v.expiry_date) {
      const daysUntil = Math.ceil((new Date(v.expiry_date) - now) / (1000 * 60 * 60 * 24));
      if (daysUntil < 0) vaccineStatus = 'expired';
      else if (daysUntil <= 30 && vaccineStatus !== 'expired') vaccineStatus = 'expiring';
    }
  });
  
  const StatusIcon = vaccineStatus === 'valid' ? CheckCircle : 
                     vaccineStatus === 'expiring' ? Clock : AlertCircle;
  const colors = STATUS_COLORS[vaccineStatus];
  
  return (
    <div className="flex gap-3">
      <button
        onClick={onVaccinesClick}
        className={`
          flex-1 flex items-center gap-3 p-4 rounded-2xl
          ${colors.bg} border border-transparent
          transition-all active:scale-[0.98]
        `}
        data-testid="summary-bar-vaccines"
      >
        <StatusIcon className={colors.icon} size={20} />
        <div className="text-left">
          <p className="text-sm font-semibold text-[#1D1D1F] dark:text-white">
            {vaccines.length} Vaccini
          </p>
          <p className={`text-xs ${colors.text}`}>
            {vaccineStatus === 'valid' ? 'Tutto ok' : 
             vaccineStatus === 'expiring' ? 'In scadenza' : 'Attenzione'}
          </p>
        </div>
      </button>
      
      <button
        onClick={onDocsClick}
        className={`
          flex-1 flex items-center gap-3 p-4 rounded-2xl
          bg-slate-50 dark:bg-slate-500/10
          transition-all active:scale-[0.98]
        `}
        data-testid="summary-bar-documents"
      >
        <FileText className="text-slate-500" size={20} />
        <div className="text-left">
          <p className="text-sm font-semibold text-[#1D1D1F] dark:text-white">
            {documents.length} Documenti
          </p>
          <p className="text-xs text-slate-500">
            Gestisci
          </p>
        </div>
      </button>
    </div>
  );
}
